import { Config } from 'config/envConfig';
import axios from 'axios';
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCircleXmark,
    faExclamationTriangle,
    faFolderPlus,
    faPlus,
    faSpider,
    faTrash,
} from '@fortawesome/free-solid-svg-icons';
import { alert, AlertType, Button, Input } from '@quyva/shared-ui';
import { getUrl } from './pdfDisplay/utils';
import { ProcessType } from '@quyva/common';
import { TaskProgressType } from '@quyva/common';
import { RepoType } from '@quyva/common';
import { isReservedRepoName } from '@quyva/common';
import { Toggle } from '@quyva/shared-ui';
import { useMaintenanceWarning } from '../hooks/useMaintenanceWarning';
import { RepoConfig } from './RepoConfig';
import { ModelTiers } from '@quyva/common';

const minimumRecrawlInterval =
    Number(Config.MINIMUM_RECRAWL_INTERVAL) || 7;

const AddInternetRepo = ({
    isAdmin,
    reponame,
    setAddNewMode,
    getAllReposData,
    socket,
    setViewNotifications,
    visionRepo,
    onChangeVision,
    visionModel,
    setVisionModel,
    embeddingModel,
    setEmbeddingModel,
    error,
    setError,
    isNameFound,
    user,
    existingRepo = false,
    onUrlsAdded,
    imageResolution,
    modelTierRepo,
    isLoadingModels,
    setModelTierRepo,
    availableCustomModels,
    fetchedUrlList,
    knowledgeGraph,
    setKnowledgeGraph,
    availableEmbeddingModels,
    modelsLoading,
}) => {
    const { warnIfNeeded, WarningModal } = useMaintenanceWarning();
    const [urls, setUrls] = useState(['']);
    const [localError, setLocalError] = useState('');
    const [autoRecrawl, setAutoRecrawl] = useState(false);
    const [showRecrawlInterval, setShowRecrawlInterval] = useState(false);
    const [recrawlInterval, setRecrawlInterval] = useState(
        minimumRecrawlInterval
    );
    const [isInternetRepo, setIsInternetRepo] = useState(true);
    const [crawlLimit, setCrawlLimit] = useState(null); // default = None
    const isEmbedded = existingRepo;

    const handleUrlChange = (index, value) => {
        const newUrls = [...urls];
        newUrls[index] = value;
        setUrls(newUrls);
        setLocalError('');
    };

    const validateUrl = (url) => {
        if (url === '') {
            alert(AlertType.WARNING, 'Please enter a URL.');
            return false;
        }
        try {
            new URL(url);
            return true;
        } catch (e) {
            return false;
        }
    };

    const validateUrls = () => {
        // Initialize seen with fetchedUrlList (if it exists), to prevent duplicates
        const seen = new Set(fetchedUrlList || []);
        for (const url of urls) {
            if (url === '') {
                alert(
                    AlertType.ERROR,
                    'Please fill in all current URL fields before adding more.'
                );

                return false;
            }
            if (!validateUrl(url)) {
                alert(AlertType.ERROR, 'One or more URLs are invalid.');
                return false;
            }
            if (seen.has(url)) {
                alert(AlertType.ERROR, 'Duplicate URLs are not allowed.');
                return false;
            }
            seen.add(url);
        }
        return true;
    };

    const addUrlField = (e) => {
        e.preventDefault();
        if (!urls.some((url) => url === '')) {
            setUrls([...urls, '']);
            setLocalError('');
        } else {
            setLocalError(
                'Please fill in and validate current URLs before adding more'
            );
        }
    };

    const removeUrlField = (index) => {
        const newUrls = urls.filter((_, i) => i !== index);
        setUrls(newUrls);
        setLocalError('');
    };

    // Handle toggle change
    const onChange = (checked) => {
        // Set autoRecrawl to true when checked, false when unchecked
        setAutoRecrawl(checked);

        // Show or hide recrawl interval based on toggle state
        setShowRecrawlInterval(checked);

        // If toggle is off, reset recrawl interval to the logic and autoRecrawl to false
        if (!checked) {
            setRecrawlInterval(minimumRecrawlInterval);
            // Reset recrawl interval
            setAutoRecrawl(false); // Set autoRecrawl to false
        }
    };

    const handleCrawlLimitChange = (e) => {
        let val = e.target.value;

        if (val === '') {
            // allow empty → treat as None
            setCrawlLimit(null);
            return;
        }

        val = Number(val);

        if (val >= 1 && val <= 1000) {
            setCrawlLimit(val);
        } else {
            // reset if invalid
            alert(AlertType.ERROR, 'Values can only be between 1 to 1000.');
            setCrawlLimit(null);
        }
    };

    // Handle recrawl interval change
    const handleRecrawlIntervalChange = (e) => {
        const min = minimumRecrawlInterval;
        const value = Math.max(min, Number(e.target.value));
        setRecrawlInterval(value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateUrls()) {
            setLocalError('Please enter valid URLs');
            return;
        }

        const isFound =
            typeof isNameFound === 'function' ? isNameFound(reponame) : false;

        if (isFound) {
            setError('Repository with this name already exists!');
            return;
        }

        if (isFound) {
            setError('Repository with this name already exists!');
            return;
        }

        // Block names that collide with the auto-generated extraction-derived
        // repo naming convention, so this repo can't be mistaken for (and
        // cascade-deleted as) a derived child of another repo.
        const reservedNameData = isReservedRepoName(reponame);
        if (reservedNameData.isReserved) {
            setError(reservedNameData.error);
            return;
        }

        const filteredUrls = urls.filter((url) => url !== '');
        if (filteredUrls.length === 0) {
            setLocalError('Please enter at least one URL');
            return;
        }

        warnIfNeeded('internet repository creation', async () => {
        try {
            if (!existingRepo) {
                // Original repo creation logic for new repos
                socket.emit('statusUpdate', {
                    message: 'Creating new repository...',
                    percentageCompletion: 0,
                    process: ProcessType.REPO_CREATION,
                    progress: TaskProgressType.STARTED,
                    repoId: null,
                    repoName: reponame,
                    statusCode: 201,
                    timeStamp: new Date(),
                    userMail: user.email,
                });
                const folderData = {
                    id: user.email,
                    name: reponame,
                    visionRepo: true,
                    visionModel: visionModel,
                    modelTier: modelTierRepo,
                    image_res: imageResolution,
                    vector_store_type: 'CHROMA',
                    summary_flag: false,
                    storage_type: RepoType.INTERNET,
                    repotype: RepoType.CONTENT,
                    knowledgeGraph: knowledgeGraph,
                    embedding_model: embeddingModel,
                };

                // Create the repository
                const repoDataString = encodeURIComponent(
                    JSON.stringify(folderData)
                );
                const encodedToken = encodeURIComponent(user.token);

                try {
                    await axios.post(
                        getUrl(
                            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/new_empty_repo/${repoDataString}`
                        ),
                        undefined,
                        {
                            headers: {
                                Authorization: encodedToken,
                            },
                        }
                    );
                } catch (err) {
                    console.error('Repository creation failed:', err.response?.data);
                    throw new Error(
                        `Failed to create repository: ${
                            err.response?.data?.error || err.message || 'Unknown error'
                        }`
                    );
                }

                // Get the repo ID immediately
                let updatedReposData;
                try {
                    const updatedReposResponse = await axios.get(
                        getUrl(
                            `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}/getAllChatsRepos/${user.email}`
                        ),
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: encodeURIComponent(user.token),
                            },
                        }
                    );
                    updatedReposData = updatedReposResponse.data;
                } catch (err) {
                    throw new Error(
                        'Failed to get repository ID after creation'
                    );
                }

                const newRepo = updatedReposData.Normal.find(
                    (r) => r.name === reponame
                );

                if (!newRepo || !newRepo.repoid) {
                    throw new Error(
                        'Failed to get repository ID after creation'
                    );
                }

                // Close modal immediately after successful creation
                setAddNewMode(false);

                // Start crawling in background
                try {
                    const crawlResponse = await fetch(
                        getUrl(
                            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/crawl/crawl_and_store`
                        ),
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: encodeURIComponent(user.token),
                            },
                            body: JSON.stringify({
                                repoName: reponame,
                                user: user.email,
                                urls: filteredUrls,
                                ...(modelTierRepo && {
                                    modelTier: modelTierRepo,
                                }),
                                ...(visionModel && {
                                    visionModel: visionModel,
                                }),
                                autoRecrawl: autoRecrawl,
                                ...(autoRecrawl && {
                                    recrawlInterval: recrawlInterval,
                                }),
                                ...(crawlLimit != null &&
                                    crawlLimit !== '' && {
                                        crawlLimit: crawlLimit,
                                    }),
                            }),
                        }
                    );

                    if (!crawlResponse.ok) {
                        throw new Error('Failed to start crawling process');
                    }
                } catch (error) {
                    console.error('Error starting crawl process:', error);
                    // Don't throw here since repo is already created
                }

                // Refresh repos list
                await getAllReposData();
                onUrlsAdded?.();
            } else {
                // Close modal immediately after successful creation/update
                setAddNewMode(false);
                socket.emit('statusUpdate', {
                    message: 'Adding URL...',
                    percentageCompletion: 0,
                    process: ProcessType.FILE_UPLOAD,
                    progress: TaskProgressType.STARTED,
                    repoId: null,
                    repoName: reponame,
                    statusCode: 201,
                    timeStamp: new Date(),
                    userMail: user.email,
                });
                // Start crawling in background
                try {
                    const crawlResponse = await fetch(
                        getUrl(
                            `http://${Config.FAST_API_HOST}:${Config.FAST_API_PORT}/crawl/crawl_and_store`
                        ),
                        {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: encodeURIComponent(user.token),
                            },
                            body: JSON.stringify({
                                repoName: reponame,
                                user: user.email,
                                urls: filteredUrls,
                                ...(modelTierRepo && {
                                    modelTier: modelTierRepo,
                                }),
                                ...(visionModel && {
                                    visionModel: visionModel,
                                }),
                                autoRecrawl: autoRecrawl,
                                ...(autoRecrawl && {
                                    recrawlInterval: recrawlInterval,
                                }),
                                fileUpload: true,
                                ...(crawlLimit != null &&
                                    crawlLimit !== '' && {
                                        crawlLimit: crawlLimit,
                                    }),
                            }),
                        }
                    );

                    if (!crawlResponse.ok) {
                        throw new Error('Failed to start crawling process');
                    }
                } catch (error) {
                    console.error('Error starting crawl process:', error);
                }

                // Refresh repos list
                await getAllReposData();
            }
        } catch (error) {
            alert(AlertType.ERROR, error.message);
            setLocalError(error.message);
        }
        }); // end warnIfNeeded callback
    };

    return (
        <form
            onSubmit={handleSubmit}
            className={existingRepo ? 'w-full h-full flex flex-col' : ''}
        >
            <div className={`flex flex-col space-y-4 bg-backgrounds-white rounded-xl p-4 border-2 border-dashed border-backgrounds-primary300 ${isEmbedded ? 'flex-1 min-h-0 overflow-auto mt-3 mb-0' : 'my-4'}`}>
                <div className='text-center text-texts-secondary300 mb-4'>
                    {existingRepo
                        ? 'Add URLs to Repository'
                        : 'Add URLs to create a Repository'}
                </div>

                {urls.map((url, index) => (
                    <div
                        key={index}
                        className={`flex items-center ${
                            existingRepo ? 'space-x-3' : 'space-x-2'
                        }`}
                    >
                        <input
                            type='text'
                            value={url}
                            onChange={(e) =>
                                handleUrlChange(index, e.target.value)
                            }
                            placeholder='Enter URL (e.g., https://example.com)'
                            className={`flex-1 p-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-backgrounds-primary300 ${
                                existingRepo ? 'min-w-[600px]' : ''
                            }`}
                        />
                        {urls.length > 1 && (
                            <Button
                                type='danger'
                                icon={faTrash}
                                onClick={() => removeUrlField(index)}
                                className='p-2'
                            />
                        )}
                    </div>
                ))}

                <Button
                    type='primary'
                    icon={faPlus}
                    text='Add URL'
                    onClick={addUrlField}
                    className='w-full'
                    disabled={urls.some((url) => url === '')}
                />
            </div>

            {!existingRepo && (
                <RepoConfig
                    isAdmin={isAdmin}
                    modelTierRepo={modelTierRepo}
                    visionModel={visionModel}
                    isLoadingModels={isLoadingModels}
                    setModelTierRepo={setModelTierRepo}
                    setVisionModel={setVisionModel}
                    embeddingModel={embeddingModel}
                    setEmbeddingModel={setEmbeddingModel}
                    availableCustomModels={availableCustomModels}
                    ModelTiers={ModelTiers}
                    knowledgeGraph={knowledgeGraph}
                    onChangeKnowledgeGraph={setKnowledgeGraph}
                    isInternetRepo={isInternetRepo}
                    crawlLimit={crawlLimit}
                    setCrawlLimit={setCrawlLimit}
                    handleCrawlLimitChange={handleCrawlLimitChange}
                    availableEmbeddingModels={availableEmbeddingModels}
                    modelsLoading={modelsLoading}
                />
            )}
            {!existingRepo && (
                <div className='flex items-center mt-4'>
                    <div className='w-1/8 font-semibold text-texts-secondary300'>
                        <label htmlFor='Automatic Recrawl'>
                            Allow automatic re-crawl
                        </label>
                    </div>
                    <div className='w-full md:w-1/6 '>
                        <Toggle checked={autoRecrawl} onChange={onChange} />
                    </div>

                    {showRecrawlInterval && (
                        <div className='flex items-center'>
                            <div className='w-1/8 font-semibold text-texts-secondary300'>
                                <label htmlFor='Recrawl Timing'>
                                    Set Recrawl Timing (in days)
                                </label>
                            </div>
                            <div className='w-full md:w-1/4 ml-8'>
                                <Input
                                    type='number'
                                    min={minimumRecrawlInterval}
                                    value={recrawlInterval}
                                    onChange={handleRecrawlIntervalChange}
                                    className='block w-auto md:w-full p-2 mt-1 rounded-xl shadow-sm focus:ring-backgrounds-primary500 focus:border-backgrounds-primary400 hover:outline-none focus:outline-backgrounds-primary200 border hover:border-backgrounds-primary300'
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
            {error && (
                <div className='flex justify-center items-center text-buttons-alert400 mt-3'>
                    <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className='mr-2'
                    />
                    {error}
                </div>
            )}

            {existingRepo && isAdmin && isInternetRepo && (
                <div className='w-full flex items-center gap-4 px-4 py-3 mt-3 mb-4 bg-backgrounds-white rounded-xl border border-backgrounds-primary200'>
                    <label className='min-w-[180px] font-semibold text-texts-secondary300'>
                        <FontAwesomeIcon
                            icon={faSpider}
                            className='mr-2 text-texts-secondary300'
                        />
                        Set Crawl Limit
                    </label>

                    <Input
                        type='number'
                        placeholder='None'
                        value={crawlLimit ?? ''}
                        onChange={handleCrawlLimitChange}
                        className='!w-32 border border-gray-300 rounded-md px-2 py-1'
                    />
                </div>
            )}

            <div className='flex items-center justify-center mt-4'>
                <div className='flex items-center justify-between w-full'>
                    <Button
                        type='danger'
                        icon={faCircleXmark}
                        text={'Cancel'}
                        title={'Cancel and close the modal'}
                        className='w-1/3'
                        onClick={() => setAddNewMode(false)}
                    />

                    <button
                        className={`flex items-center justify-center rounded-full py-1.5 px-2.5 rounded-lg order bg-backgrounds-white shadow-inner border font-semibold w-1/3 ${
                            !reponame ||
                            !urls.some((url) => url !== '') ||
                            error ||
                            localError
                                ? 'text-texts-secondary300 cursor-not-allowed'
                                : 'hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200 text-backgrounds-primary400'
                        }`}
                        type='submit'
                        title={
                            existingRepo
                                ? 'Add URLs to repository'
                                : 'Create URLs repository'
                        }
                        disabled={
                            !reponame ||
                            !urls.some((url) => url !== '') ||
                            error ||
                            localError
                        }
                    >
                        <FontAwesomeIcon icon={faFolderPlus} className='mr-2' />
                        {existingRepo ? 'Add URLs' : 'Add'}
                    </button>
                </div>
            </div>
        {WarningModal}
        </form>
    );
};

export default AddInternetRepo;
